import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import {
  getMyOrganization,
  getOrgDashboard,
  inviteUrlFor,
  joinOrganization,
  listSchoolReviews,
  reclaimOrgSeat,
  removeOrgMember,
  rotateOrgInvite,
} from '../../api/organizationApi';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import './SchoolPlan.css';
import './review/SchoolReview.css';
import { DemoSchoolBanner, DemoSchoolCreateCard } from './review/DemoSchoolPanel';
import { ReclaimDialog, SeatHistoryCard, reclaimToast } from './SeatReclaim';
import { SCHOOL_REVIEW_MANUAL_PDF, useAdminShell } from './admin/adminMenu';

export const ACTIVITY_LABEL = {
  week: '이번 주 사용',
  month: '이번 달 사용',
  none: '아직 안 씀',
};

const formatJoined = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}월 ${date.getDate()}일 합류`;
};

const displayName = (member) => member.display_name || (member.email || '').split('@')[0] || '이름 없음';

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  document.body.removeChild(area);
};

const ConfirmDialog = ({ title, body, confirmLabel, onConfirm, onCancel, busy }) => (
  <div className="school-dialog-backdrop" role="presentation" onClick={onCancel}>
    <div className="school-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
      <h2>{title}</h2>
      <p>{body}</p>
      <div className="school-dialog-actions">
        <button type="button" className="school-btn-secondary" onClick={onCancel} disabled={busy}>취소</button>
        <button type="button" className="school-btn-primary" onClick={onConfirm} disabled={busy}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

export const SeatsCard = ({ seats, primary, onCopy, onRotate }) => {
  const limit = seats.seat_limit || 0;
  const filled = seats.seats_filled || 0;
  const percent = limit ? Math.min(100, Math.round((filled / limit) * 100)) : 0;
  const open = seats.seats_open || 0;
  return (
    <section className="school-card school-seats" aria-label="자리 현황">
      <div>
        <p className="school-seats-numbers" data-testid="school-seats-numbers">
          <strong>{filled}</strong>
          <span>/ {limit}명 합류</span>
        </p>
        <p className="school-seats-caption">
          {open > 0 ? (
            <>빈 자리 <b>{open}개</b>가 남았어요. 링크를 보내 선생님을 모셔요.</>
          ) : (
            <>자리가 모두 찼어요.</>
          )}
        </p>
      </div>
      <div className="school-seats-actions">
        <button type="button" className={primary ? 'school-btn-primary' : 'school-btn-secondary'} onClick={onCopy}>초대 링크 복사</button>
        <button type="button" className="school-btn-ghost" onClick={onRotate}>링크 바꾸기</button>
      </div>
      <div
        className="school-progress"
        role="progressbar"
        aria-label="합류한 선생님 비율"
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={filled}
      >
        <div className="school-progress-bar" style={{ width: `${percent}%` }} data-testid="school-progress-fill" />
      </div>
      <p className="school-seats-foot">
        {seats.seats_released > 0 && <span>내보낸 선생님이 쓴 자리 {seats.seats_released}개</span>}
        {seats.seats_unavailable > 0 && <span>등록 기간이 지난 자리 {seats.seats_unavailable}개</span>}
        <Link to="/bulk-purchase?intent=quote">자리 더 받기</Link>
      </p>
    </section>
  );
};

export const UsageCard = ({ usage }) => {
  const [period, setPeriod] = useState('week');
  const rows = usage?.[period] || [];
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <section className="school-card" aria-label="기능별 사용 합계">
      <h2 className="school-section-title">
        기능별 사용
        <span className="school-tabs" role="tablist" aria-label="기간">
          {[['week', '이번 주'], ['month', '이번 달']].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={period === id} className={period === id ? 'is-active' : ''} onClick={() => setPeriod(id)}>
              {label}
            </button>
          ))}
        </span>
      </h2>
      {rows.length === 0 ? (
        <p className="school-empty">{period === 'week' ? '이번 주' : '이번 달'}에는 아직 사용한 기록이 없어요.</p>
      ) : (
        <ul className="school-usage">
          {rows.map((row) => (
            <li key={row.category}>
              <span>{row.label}</span>
              <span className="school-usage-count">{row.count.toLocaleString()}건</span>
              <span className="school-usage-track" aria-hidden="true">
                <span style={{ width: `${Math.round((row.count / max) * 100)}%` }} />
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="school-usage-foot">건수만 보여요. 선생님이 만든 생기부·채점 내용은 관리자에게 보이지 않아요.</p>
    </section>
  );
};

export const ReviewEntryCard = ({ reviews, primary = true }) => {
  if (reviews === 'plan') {
    return (
      <section className="school-card school-dash-review is-plan" data-testid="school-review-entry">
        <div>
          <p className="school-dash-label">생기부 검토 <span className="school-tier">12개월 플랜</span></p>
          <p className="school-dash-main">선생님이 쓴 글을 직접 올리고, 고칠 곳을 한 화면에서 알려요</p>
        </div>
        <Link className="school-btn-secondary school-dash-btn" to="/pricing#school-review">생기부 검토 알아보기</Link>
      </section>
    );
  }
  const latest = reviews[0];
  return (
    <section className="school-card school-dash-review" data-testid="school-review-entry">
      <div>
        <p className="school-dash-label">생기부 검토</p>
        <p className="school-dash-main">{latest ? latest.title : '아직 시작한 검토가 없어요'}</p>
        <p className="school-dash-sub">
          {latest?.summary?.students
            ? `학생 ${latest.summary.students.toLocaleString()}명 · 글 ${(latest.summary.entries || 0).toLocaleString()}개`
            : '검토를 만들면 선생님마다 쓴 글을 직접 올려요.'}
        </p>
        <a className="school-dash-guide" href={SCHOOL_REVIEW_MANUAL_PDF} target="_blank" rel="noopener noreferrer">사용 설명서 보기</a>
      </div>
      <Link className={`${primary ? 'school-btn-primary' : 'school-btn-secondary'} school-dash-btn`} to={latest ? `/school-admin/review/${latest.id}` : '/school-admin/review'}>
        {latest ? '검토 이어 하기' : '검토 시작하기'}
      </Link>
    </section>
  );
};

const SchoolAdminDashboardPage = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const { fetchSubscriptionStatus } = useSubscription();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [manage, setManage] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const shell = useAdminShell();

  const orgId = me?.organization?.role === 'admin' ? me.organization.id : null;

  const load = useCallback(async () => {
    if (!currentUser) return;
    setError('');
    try {
      const mine = await getMyOrganization(currentUser);
      setMe(mine || {});
      const target = mine?.organization?.role === 'admin' ? mine.organization.id : null;
      if (target) {
        listSchoolReviews(currentUser, target)
          .then((result) => setReviews(result.reviews || []))
          .catch((err) => setReviews(err.status === 403 ? 'plan' : null));
        setData(await getOrgDashboard(currentUser, target));
      }
    } catch (loadError) {
      setError(loadError.message);
      setMe((prev) => prev || {});
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCopy = async () => {
    if (!data?.org?.invite_token) return;
    try {
      await copyText(inviteUrlFor(data.org.invite_token));
      showToast('초대 링크를 복사했어요. 메신저로 선생님들께 보내 주세요.', 'success', 4000);
    } catch (_error) {
      showToast('복사하지 못했어요. 링크를 길게 눌러 복사해 주세요.', 'error', 4000);
    }
  };

  const handleRotate = async () => {
    setBusy(true);
    try {
      const { invite_token: token } = await rotateOrgInvite(currentUser, orgId);
      setData((prev) => ({ ...prev, org: { ...prev.org, invite_token: token } }));
      showToast('새 링크로 바꿨어요. 예전 링크는 이제 열리지 않아요.', 'success', 4000);
    } catch (rotateError) {
      showToast(rotateError.message, 'error', 4000);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleRemove = async (member) => {
    const previous = data;
    setBusy(true);
    setData((prev) => ({
      ...prev,
      members: prev.members.filter((m) => m.uid !== member.uid),
      seats: {
        ...prev.seats,
        seats_filled: Math.max(0, prev.seats.seats_filled - (member.has_seat ? 1 : 0)),
        seats_released: (prev.seats.seats_released || 0) + (member.has_seat ? 1 : 0),
      },
    }));
    try {
      await removeOrgMember(currentUser, orgId, member.uid);
      showToast(`${displayName(member)} 선생님을 학교에서 내보냈어요.`, 'success', 4000);
    } catch (removeError) {
      setData(previous);
      showToast(removeError.message, 'error', 4000);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleReclaim = async (member, payload) => {
    setBusy(true);
    try {
      const result = await reclaimOrgSeat(currentUser, orgId, member.uid, payload);
      showToast(reclaimToast(displayName(member), result), 'success', 6000);
      setConfirm(null);
      await load();
    } catch (reclaimError) {
      showToast(reclaimError.message, 'error', 5000);
    } finally {
      setBusy(false);
    }
  };

  const handleClaimMySeat = async () => {
    setBusy(true);
    try {
      const result = await joinOrganization(currentUser, data.org.invite_token);
      showToast(result?.message || '', result?.ok ? 'success' : 'error', 5000);
      if (result?.ok) {
        fetchSubscriptionStatus?.(true);
        await load();
      }
    } catch (claimError) {
      showToast(claimError.message, 'error', 5000);
    } finally {
      setBusy(false);
    }
  };

  const loading = !me && !error;
  const notAdmin = me && !orgId;
  const myRow = data?.members?.find((m) => m.uid === currentUser?.uid);
  const shellView = shell?.view;
  const shellNonce = shell?.nonce;
  const loaded = Boolean(data);
  useEffect(() => {
    if (!loaded || !shell) return;
    if (shellView !== 'members' && shellView !== 'usage') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (shellView === 'usage') setShowUsage(true);
    const target = shellView === 'usage' ? '.school-dash-more' : '.school-dash-members';
    window.setTimeout(() => document.querySelector(target)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 50);
  }, [shellView, shellNonce, loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const inviteFirst = Boolean(data && !data.org.demo && data.members.length <= 1 && (data.seats?.seats_open || 0) > 0);
  const showMySeat = Boolean(myRow && !myRow.has_seat && (data?.seats?.seats_open || 0) > 0);

  return (
    <main className="school-page school-dash">
      <Helmet>
        <title>학교 관리자 | 왓퀴즈</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {error && <p className="school-error" role="alert">{error}</p>}

      {loading && (
        <>
          <div className="school-card school-skeleton" style={{ minHeight: 200 }} />
          <div className="school-grid">
            <div className="school-card school-skeleton" style={{ minHeight: 320 }} />
            <div className="school-card school-skeleton" style={{ minHeight: 320 }} />
          </div>
        </>
      )}

      {notAdmin && !error && (
        <section className="school-card school-status-card" data-testid="school-admin-not-admin">
          <h2>학교 관리자만 볼 수 있어요</h2>
          <p>12개월 · 선생님 10명 이상 구매한 학교의 대표 선생님이라면 관리자 신청을 해 주세요.</p>
          <Link className="school-btn-primary" to="/school-admin/apply">관리자 신청하기</Link>
        </section>
      )}
      {notAdmin && !error && <DemoSchoolCreateCard currentUser={currentUser} />}

      {orgId && !data && !error && <div className="school-card school-skeleton" style={{ minHeight: 200 }} />}

      {orgId && data && (
        <>
          {data.org.demo && <DemoSchoolBanner currentUser={currentUser} />}
          <header className="school-dash-head">
            <div>
              <h1>{data.org.name}</h1>
              <p className="school-dash-tier">
                학교 관리자 · {data.org.plan_tier === 'enterprise' ? '학교(엔터프라이즈)' : '학교'}
              </p>
            </div>
          </header>

          {reviews && reviews !== 'plan' && !inviteFirst && <ReviewEntryCard reviews={reviews} />}

          {!data.org.demo && (
            <SeatsCard
              seats={data.seats}
              primary={inviteFirst}
              onCopy={handleCopy}
              onRotate={() => setConfirm({ type: 'rotate' })}
            />
          )}

          {reviews && reviews !== 'plan' && inviteFirst && <ReviewEntryCard reviews={reviews} primary={false} />}

          {showMySeat && (
            <section className="school-card school-my-seat" data-testid="school-my-seat">
              <p>
                <strong>선생님 자리는 아직 비어 있어요.</strong>
                <span>한 자리를 쓰면 선생님도 PRO로 바로 쓸 수 있어요.</span>
              </p>
              <button type="button" className="school-btn-secondary" onClick={handleClaimMySeat} disabled={busy}>
                내 자리 받기
              </button>
            </section>
          )}

          {reviews === 'plan' && <ReviewEntryCard reviews={reviews} />}

          <section className="school-card school-dash-members" aria-label="선생님 목록">
            <h2 className="school-section-title">
              <span>
                선생님 {data.members.length}명
                <small>이번 주 사용 {data.active_this_week || 0}명</small>
              </span>
              {data.members.some((member) => member.role !== 'admin') && (
                <button type="button" className="school-btn-secondary school-btn-sm" aria-pressed={manage} onClick={() => setManage((v) => !v)}>
                  {manage ? '관리 끝내기' : '선생님 관리'}
                </button>
              )}
            </h2>
            {data.members.length <= 1 && (
              <p className="school-empty">아직 합류한 선생님이 없어요. 초대 링크를 보내 보세요.</p>
            )}
            <ul className="school-members">
              {data.members.map((member) => (
                <li key={member.uid} className="school-member" data-testid="school-member">
                  <span className="school-avatar" aria-hidden="true">{displayName(member).slice(0, 1)}</span>
                  <span className="school-member-main">
                    <span className="school-member-name">
                      {displayName(member)}
                      {member.role === 'admin' && <span className="school-member-role">관리자</span>}
                    </span>
                    <span className="school-member-meta">
                      {[formatJoined(member.joined_at), member.email].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="school-member-side">
                    {!manage && (
                      <span className={`school-activity school-activity--${member.activity}`}>
                        {ACTIVITY_LABEL[member.activity] || ACTIVITY_LABEL.none}
                      </span>
                    )}
                    {manage && member.role !== 'admin' && member.has_seat && !data.org.demo && (
                      <button
                        type="button"
                        className="school-btn-secondary school-btn-sm"
                        aria-label={`${displayName(member)} 선생님 자리 회수`}
                        onClick={() => setConfirm({ type: 'reclaim', member })}
                      >
                        자리 회수
                      </button>
                    )}
                    {manage && member.role !== 'admin' && (
                      <button
                        type="button"
                        className="school-btn-secondary school-btn-sm"
                        aria-label={`${displayName(member)} 선생님 내보내기`}
                        onClick={() => setConfirm({ type: 'remove', member })}
                      >
                        내보내기
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <button type="button" className="school-dash-more" aria-expanded={showUsage} onClick={() => setShowUsage((v) => !v)}>
            {showUsage ? '사용 기록 접기' : '사용 기록 보기'}
          </button>
          {showUsage && (
            <div className="school-dash-usage">
              <UsageCard usage={data.usage} />
              <SeatHistoryCard events={data.seat_events} credits={data.seat_credits} />
            </div>
          )}
        </>
      )}

      {confirm?.type === 'rotate' && (
        <ConfirmDialog
          title="초대 링크를 바꿀까요?"
          body="지금 링크는 더 이상 열리지 않아요. 이미 합류한 선생님은 그대로예요."
          confirmLabel="링크 바꾸기"
          onConfirm={handleRotate}
          onCancel={() => setConfirm(null)}
          busy={busy}
        />
      )}

      {confirm?.type === 'remove' && (
        <ConfirmDialog
          title={`${displayName(confirm.member)} 선생님을 내보낼까요?`}
          body="학교 소속만 풀려요. 이미 받은 PRO 기간은 그대로 남고, 쓴 자리는 돌아오지 않아요. 남은 기간을 돌려받으려면 자리 회수를 써 주세요."
          confirmLabel="내보내기"
          onConfirm={() => handleRemove(confirm.member)}
          onCancel={() => setConfirm(null)}
          busy={busy}
        />
      )}

      {confirm?.type === 'reclaim' && (
        <ReclaimDialog
          name={displayName(confirm.member)}
          onConfirm={(payload) => handleReclaim(confirm.member, payload)}
          onCancel={() => setConfirm(null)}
          busy={busy}
        />
      )}
    </main>
  );
};

export default SchoolAdminDashboardPage;
